import { useState, useEffect } from 'react';

/**
 * Hook to detect if the current viewport is mobile-sized.
 * 
 * @param breakpoint - Width threshold in pixels to consider as mobile (default: 768px)
 * @returns Boolean indicating if viewport width is below the breakpoint
 */
export const useMobileView = (breakpoint = 768): boolean => {
  // Initialize with current window width
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // Only access window when in browser environment
    if (typeof window !== 'undefined') {
      return window.innerWidth < breakpoint;
    }
    return false;
  });

  useEffect(() => {
    // Skip if not in browser environment
    if (typeof window === 'undefined') return;

    // Handler to call on window resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call handler right away to update state with initial window size
    handleResize();
    
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]); // Re-run effect if breakpoint changes

  return isMobile;
};

export default useMobileView; 